import {Component, computed, effect, inject, signal} from '@angular/core';
import {CreateTodoStatusRequest, TodoStatusSummary, UpdateTodoStatusRequest} from '../../../../../../../api';
import {FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ColorPickerModule} from 'primeng/colorpicker';
import {CheckBox} from '../../../../../../components/check-box/check-box';
import {TodoStatusFormModel} from '../../../../../todos/models/todo-status-form-model';
import {TextInput} from '../../../../../../components/text-input/text-input';
import {TranslatePipe} from '@ngx-translate/core';
import {ColorPicker} from '../../../../../../components/color-picker/color-picker';
import {ElciaButton} from '../../../../../../components/buttons/button/button';
import {TodoStoreService} from '../../../../../todos/services/todo-store-service';
import {TodoQueryService} from '../../../../../todos/services/todo-query.service';
import {TodoCommandService} from '../../../../../todos/services/todo-command.service';
import {ConfirmService} from '../../../../../../services/confirm.service';
import {CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';

@Component({
  selector: 'elcia-todo-settings-status',
  imports: [
    ReactiveFormsModule,
    ColorPickerModule,
    CheckBox,
    TextInput,
    ElciaButton,
    TranslatePipe,
    ColorPicker,
    CdkDrag,
    CdkDropList,
    CdkDragPlaceholder,
  ],
  providers: [TodoStoreService, TodoQueryService, TodoCommandService],
  templateUrl: './todo-settings-status.html',
  styleUrl: './todo-settings-status.scss',
})

export class TodoSettingsStatus {

  private readonly todoStore = inject(TodoStoreService);
  private readonly confirmService = inject(ConfirmService);

  isLoading = computed(() => this.todoStore.isLoadingStatusSummaries());
  todoStatuses = computed(() => this.todoStore.statusSummaries());

  statusFormArray = signal(new FormArray<FormGroup<TodoStatusFormModel>>([]));

  private readonly syncStatusForms = effect(() => {
    const statuses = this.todoStatuses();
    const formArray = new FormArray<FormGroup<TodoStatusFormModel>>([]);

    for (const status of statuses) {
      const formGroup = this.createFormGroupByStatus(status);
      formGroup.disable();
      formArray.push(formGroup);
    }

    this.statusFormArray.set(formArray);
  });

  //#region Creation form

  formBuilder = inject(FormBuilder);
  newStatusForm = this.createNewform();


  createNewform() {
    return this.formBuilder.nonNullable.group({
      nameEn: this.formBuilder.nonNullable.control('', [Validators.required]),
      nameFr: this.formBuilder.nonNullable.control('', [Validators.required]),
      nameNl: this.formBuilder.nonNullable.control('', [Validators.required]),
      color: this.formBuilder.nonNullable.control('#000000'),
      mustArchive: this.formBuilder.nonNullable.control(false),
      isDefault: this.formBuilder.nonNullable.control(false)
    })
  };

  createFormGroupByStatus(status: TodoStatusSummary) {
    return new FormGroup<TodoStatusFormModel>({
      id: new FormControl(status?.id ?? 0),
      nameEn: new FormControl(status?.nameEn ?? '', Validators.required),
      nameFr: new FormControl(status?.nameFr ?? '', Validators.required),
      nameNl: new FormControl(status?.nameNl ?? '', Validators.required),
      color: new FormControl(status?.color ?? '#808080', Validators.required),
      isDefault: new FormControl(status?.isDefault ?? false, Validators.required),
      mustArchive: new FormControl(status?.mustArchive ?? false, Validators.required),
      orderNum: new FormControl(status?.orderNum ?? -1)
    });
  }

  //#endregion

  deleteStatus(id: number) {
    const status = this.todoStore.statusSummaries().find((s) => s.id === id)!;

    const message = status.nameFr ?? status.nameEn ?? status.nameNl ?? '';
    this.confirmService.confirm({
      message: message,
      title: 'todo.status',
      accept: () => this.todoStore.deleteTodoStatusMutation.mutate(id),
    });
  }

  saveForm() {
    const request: CreateTodoStatusRequest = {
      nameFr: this.newStatusForm.controls.nameFr.value,
      nameNl: this.newStatusForm.controls.nameNl.value,
      nameEn: this.newStatusForm.controls.nameEn.value,
      color: this.newStatusForm.controls.color.value,
      mustArchive: this.newStatusForm.controls.mustArchive.value!,
      orderNum: -1,
      isDefault: this.newStatusForm.controls.isDefault.value!
    };

    this.todoStore.createTodoStatusMutation.mutate(request);
    this.newStatusForm = this.createNewform();
  };

  private getFormGroupById(statusId: number | null | undefined) {
    return this.statusFormArray().controls.find((c) => c.value.id == statusId);
  }

  toggleFormGroupEnableByStatusId(statusId: number | null | undefined) {
    const formGroup = this.getFormGroupById(statusId);
    if (formGroup === null || formGroup === undefined) return;

    if (formGroup.enabled) {
      this.saveFormGroup(formGroup);
    } else {
      formGroup.enable();
    }
  }

  editFormGroupByStatusId(statusId: number | null | undefined) {
    const formGroup = this.getFormGroupById(statusId);
    formGroup?.enable();
  }

  saveFormGroup(formGroup: FormGroup) {
    formGroup?.disable();
    if (formGroup === null) return;

    const updateRequest: UpdateTodoStatusRequest = {
      nameFr: formGroup.value.nameFr,
      nameNl: formGroup.value.nameNl,
      nameEn: formGroup.value.nameEn,
      color: formGroup.value.color,
      mustArchive: formGroup.value.mustArchive,
      isDefault: formGroup.value.isDefault,
      orderNum: formGroup.value.orderNum ?? 0
    };

    this.todoStore.updateTodoStatusMutation.mutate({id: formGroup.value.id, body: updateRequest});
  }

  onDrop(event: CdkDragDrop<FormGroup<TodoStatusFormModel>[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const statusId = event.container.data[event.previousIndex].value.id;
    if (statusId == null) {
      return;
    }

    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

    this.todoStore.reorderTodoStatusesMutation.mutate({
      statusId,
      newOrderNumber: event.currentIndex,
    });
  }

}
